import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ENDPOINT } from "constants/variables";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import SendIcon from "@mui/icons-material/Send";
import Textarea from "@mui/joy/Textarea";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import { helpHttp } from "service/HelpHttp";
import { setLoading } from "reducers/loadingSlice";
import { LoadingNews } from "components/utils/LoadingNews";
import { successErrorAction, warningErrorAction } from "actions/ErrorActions";
import generatePurchaseSendEmailPdf from "pages/purchaseDelivery/purchaseSendEmailList/generatePurchaseSendEmailPdf";
import "css/generalSettings.css";
import "css/customDialog.css";

const INITIAL_PURCHASES = [];
const INITIAL_EMAIL_STATE = {
  id: "",
  defaultStore_id: 0,
  useraccount_id: 0,
  store_EmailTo: "",
  subjectEmail: "ENVÍO DE ALBARANES UNIFICADOS",
  contentEmail: "",
  filetosend: [],
  fileName: "ALBARANES_UNIFICADOS.pdf",
  fileType: "",
};

const INITIAL_EMAIL_OPTIONS = [{ id: "", email: "" }];
const INVALID_CHARS_REGEX = /[\\!$%&|<>?{}~"']+/;
const CLEAN_SEARCH_REGEX = /[.;\-/\\|]/g;

function useDebouncedCallback(callback, delay = 300) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef(null);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);
  return useCallback(
    (...args) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current?.(...args);
      }, delay);
    },
    [delay]
  );
}

const PurchaseDeliverySendEmailList = ({
  getDataToPDF,
  showModalPurchaseSendEmailList,
  typeOfList
}) => {
  const isModalOpen = Boolean(showModalPurchaseSendEmailList);
  const { t, i18n } = useTranslation(["listDataPurchases"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.loginUser.token);
  const defaultStore = useSelector((state) => state.loginUser.defaultStore);
  const useraccount_id = useSelector((state) => state.loginUser.useraccount_id);
  const footeremail = useSelector((state) => state.loginUser.defaultticketsformat.footeremail);
  const headerPrinterPurchase = useSelector((state) => state.loginUser.defaultticketsformat.headerPrinterPurchase);
  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const [purchases, setPurchases] = useState(INITIAL_PURCHASES);
  const [formEMAIL, setFormEMAIL] = useState(INITIAL_EMAIL_STATE);
  const [errorsEMAIL, setErrorsEMAIL] = useState({});
  const [pdfBlob, setPdfBlob] = useState(null);
  const [emailToSelect, setEmailToSelect] = useState(INITIAL_EMAIL_OPTIONS);
  const [filteredEmail, setFilteredEmail] = useState([]);
  const [highlightedIndexEmail, setHighlightedIndexEmail] = useState(-1);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isEndedList = useMemo(
    () => typeOfList !== "1" && typeOfList !== "3",
    [typeOfList]
  );

  const targetRoute = useMemo(() => {
    if (typeOfList === "1") return "/purchasedelivery";
    if (typeOfList === "2") return "/purchasedeliveryended";
    return "/purchasedeliverybegin";
  }, [typeOfList]);

  const hasPurchasesWithLines = useMemo(() => {
    if (!Array.isArray(purchases)) return false;
    return purchases.some(
      (item) =>
        Boolean(item?.id) &&
        Array.isArray(item?.linespurchasedelivery) &&
        item.linespurchasedelivery.length > 0
    );
  }, [purchases]);

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return "";
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const emailToWidth = useMemo(
    () => (i18n.language === "es" ? 738 : 867),
    [i18n.language]
  );

  const subjectWidth = useMemo(
    () => (i18n.language === "es" ? 941 : 937),
    [i18n.language]
  );

  const resetLocalState = useCallback(() => {
    setPurchases(INITIAL_PURCHASES);
    setFormEMAIL(INITIAL_EMAIL_STATE);
    setErrorsEMAIL({});
    setPdfBlob(null);
    setEmailToSelect(INITIAL_EMAIL_OPTIONS);
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
  }, []);

  const resetStateAndClose = useCallback(() => {
    resetLocalState();
    dispatch(setLoading(false));
    navigate(targetRoute);
  }, [dispatch, navigate, resetLocalState, targetRoute]);

  const validateFormEmail = useCallback((currentFormEmail) => {
    const data = currentFormEmail || INITIAL_EMAIL_STATE;
    const errors = {};
    if (!data.store_EmailTo) {
      errors.store_EmailTo = "Text_FieldSendEmailToRequired";
    } else if (INVALID_CHARS_REGEX.test(data.store_EmailTo)) {
      errors.store_EmailTo = "Text_TextNoValid";
    }
    if (INVALID_CHARS_REGEX.test(data.subjectEmail || "")) {
      errors.subjectEmail = "Text_TextNoValid";
    }
    if (INVALID_CHARS_REGEX.test(data.contentEmail || "")) {
      errors.contentEmail = "Text_TextNoValid";
    }
    return errors;
  }, []);

  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        resolve(String(reader.result || "").split(",")[1] || "");
      };
      reader.onerror = reject;
    });
  }, []);

  const scrollToElement = useCallback((index) => {
    const dropdown = document.querySelector(".email-suggest-dropdown");
    const item = dropdown?.children?.[index];
    if (item) {
      item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  const runFilterEmail = useCallback(
    (searchValue) => {
      const query = (searchValue || "").toLowerCase().trim();
      if (!query) {
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
        return;
      }
      const filtered = (Array.isArray(emailToSelect) ? emailToSelect : [])
        .filter((item) => item?.email)
        .filter((item) => item.email.toLowerCase().includes(query))
        .slice(0, 20);
      setFilteredEmail(filtered);
      setHighlightedIndexEmail(filtered.length > 0 ? 0 : -1);
    },
    [emailToSelect]
  );

  const debouncedHandleSearchEmail = useDebouncedCallback(runFilterEmail, 300);
  const handleSelectEmail = useCallback((emailObj) => {
    if (!emailObj) return;
    setFormEMAIL((prev) => ({
      ...prev,
      store_EmailTo: emailObj.email || "",
    }));
    setFilteredEmail([]);
    setHighlightedIndexEmail(-1);
  }, []);

  const handleKeyDownEmail = useCallback(
    (event) => {
      if (event.key === "ArrowDown") {
        if (filteredEmail.length === 0 && !(formEMAIL.store_EmailTo || "").trim()) {
          setFilteredEmail(emailToSelect);
          setHighlightedIndexEmail(0);
          return;
        }
        setHighlightedIndexEmail((prevIndex) => {
          const nextIndex =
            prevIndex < filteredEmail.length - 1 ? prevIndex + 1 : prevIndex;
          scrollToElement(nextIndex);
          return nextIndex;
        });
        return;
      }
      if (event.key === "ArrowUp") {
        setHighlightedIndexEmail((prevIndex) => {
          const previousIndex = prevIndex > 0 ? prevIndex - 1 : prevIndex;
          scrollToElement(previousIndex);
          return previousIndex;
        });
        return;
      }
      if ((event.key === "Enter" || event.key === "Tab") && highlightedIndexEmail >= 0) {
        if (highlightedIndexEmail < filteredEmail.length) {
          event.preventDefault();
          handleSelectEmail(filteredEmail[highlightedIndexEmail]);
        }
        return;
      }
      if (event.key === "Escape") {
        const input = (formEMAIL.store_EmailTo || "").toLowerCase();
        const isValid = (Array.isArray(emailToSelect) ? emailToSelect : []).some(
          (item) => (item?.email || "").toLowerCase() === input
        );
        if (!isValid) {
          setFormEMAIL((prev) => ({
            ...prev,
            store_EmailTo: "",
          }));
        }
        setFilteredEmail([]);
        setHighlightedIndexEmail(-1);
      }
    },
    [
      emailToSelect,
      filteredEmail,
      formEMAIL.store_EmailTo,
      highlightedIndexEmail,
      handleSelectEmail,
      scrollToElement,
    ]
  );

  const handleBlurSuggestions = useCallback((event) => {
    if (!event?.currentTarget) return;
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFilteredEmail([]);
      setHighlightedIndexEmail(-1);
    }
  }, []);

  const handleChangeDataEditEmail = useCallback((event) => {
    const { name, value } = event.target;
    setFormEMAIL((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleBlurDataEditEmail = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormEMAIL((prev) => {
        const nextForm = {
          ...prev,
          [name]: value,
        };
        setErrorsEMAIL(validateFormEmail(nextForm));
        return nextForm;
      });
    },
    [validateFormEmail]
  );

  const onChangeEmailTo = useCallback(
    (event) => {
      const nextValue = (event.target.value || "").toLowerCase();
      setFormEMAIL((prev) => ({
        ...prev,
        store_EmailTo: nextValue,
      }));
      debouncedHandleSearchEmail(nextValue);
    },
    [debouncedHandleSearchEmail]
  );

  const handleSubmitSendEmail = useCallback(async () => {
    const errors = validateFormEmail(formEMAIL);
    setErrorsEMAIL(errors);
    const hasNoErrors = Object.keys(errors).length === 0;
    const hasEmailTo = Boolean(formEMAIL?.store_EmailTo);
    const hasPdf = Boolean(pdfBlob);
    if (!hasNoErrors || !hasEmailTo || !hasPdf) {
      if (errors.store_EmailTo) {
        dispatch(warningErrorAction(t(errors.store_EmailTo)));
      } else if (errors.subjectEmail) {
        dispatch(warningErrorAction(t(errors.subjectEmail)));
      } else if (errors.contentEmail) {
        dispatch(warningErrorAction(t(errors.contentEmail)));
      } else if (!hasPdf) {
        dispatch(warningErrorAction(t("Text_FieldSendFileRequired")));
      } else {
        dispatch(warningErrorAction(t("Text_IncorrectData")));
      }
      return;
    }
    dispatch(setLoading(true));
    try {
      const base64File = await blobToBase64(pdfBlob);
      const payload = {
        id: "",
        defaultStore_id: defaultStore.id,
        useraccount_id,
        store_EmailTo: formEMAIL.store_EmailTo,
        subjectEmail: formEMAIL.subjectEmail,
        contentEmail: formEMAIL.contentEmail,
        filetosend: base64File,
        fileName: "ALBARANES_UNIFICADOS.pdf",
        fileType: "application/pdf",
      };
      const response = await helpHttp().postemail(
        ENDPOINT,
        "api/sendemailwithattachment",
        token,
        payload
      );
      if (response?.status === "OK") {
        dispatch(successErrorAction(t("Text_EMAIL_Sended")));
      } else {
        dispatch(warningErrorAction(t(response?.cause || "Text_IncorrectData")));
      }
      dispatch(setLoading(false));
      resetStateAndClose();
    } catch {
      dispatch(setLoading(false));
      dispatch(warningErrorAction(t("Text_IncorrectData")));
      resetStateAndClose();
    } 
  }, [
    blobToBase64,
    defaultStore.id,
    dispatch,
    formEMAIL,
    pdfBlob,
    resetStateAndClose,
    t,
    token,
    useraccount_id,
    validateFormEmail,
  ]);

  useEffect(() => {
    if (!isModalOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        resetStateAndClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen, resetStateAndClose]);

  useEffect(() => {
    if (!isModalOpen) return;
    const raw = formEMAIL.store_EmailTo || "";
    const search = raw ? raw.replace(CLEAN_SEARCH_REGEX, "").trim() : "-1";
    if (!search || search === "-1") {
      setEmailToSelect(INITIAL_EMAIL_OPTIONS);
      setFilteredEmail([]);
      setHighlightedIndexEmail(-1);
      return;
    }
    let cancelled = false;
    const fetchEmails = async () => {
      try {
        const response = await helpHttp().get2(
          ENDPOINT,
          "api/getlistemails/",
          token,
          search
        );
        if (cancelled || !mountedRef.current) return;
        if (response?.err) {
          dispatch(warningErrorAction(t("Text_InvalidCredentials")));
          setEmailToSelect(INITIAL_EMAIL_OPTIONS);
          return;
        }
        setEmailToSelect(Array.isArray(response) ? response : INITIAL_EMAIL_OPTIONS);
      } catch {
        if (!cancelled && mountedRef.current) {
          dispatch(warningErrorAction(t("Text_IncorrectData")));
        }
      }
    };
    fetchEmails();
    return () => {
      cancelled = true;
    };
  }, [dispatch, formEMAIL.store_EmailTo, isModalOpen, t, token]);

  useEffect(() => {
    if (!isModalOpen) return;
    let cancelled = false;
    const load = async () => {
      dispatch(setLoading(true));
      try {
        if (!getDataToPDF) {
          if (cancelled || !mountedRef.current) return;
          setPurchases([]);
          setPdfBlob(null);
          setFormEMAIL({
            ...INITIAL_EMAIL_STATE,
            defaultStore_id: defaultStore.id,
            useraccount_id,
            contentEmail: footeremail || "",
          });
          return;
        }
        const response = await helpHttp().post11(
          ENDPOINT,
          "purchasedelivery/purchasedeliverytopdfs",
          token,
          isEndedList,
          getDataToPDF?.selected || []
        );
        if (cancelled || !mountedRef.current) return;
        if (response?.err) {
          setPurchases([]);
          setPdfBlob(null);
          dispatch(
            warningErrorAction(
              t(
                response.message === "Load failed"
                  ? "Text_ConnectionFailied"
                  : "Text_InvalidCredentials"
              )
            )
          );
          return;
        }
        const normalized = Array.isArray(response)
          ? response
          : response
            ? [response]
            : [];
        const emails = [
          ...new Set(
            normalized
              .map((item) => item?.ori_tras_email?.trim())
              .filter((email) => email)
          ),
        ].join(";"); 
        const albList = [
          ...new Set(
            normalized
              .map((purchase) => {
                const formattedId = String(purchase?.purchasenote ?? "").padStart(8, "0");
                return `ALB_${formattedId}.PDF`;
              })
              .filter(Boolean)
          ),
        ].join("\n");
        const footeremailUpdated =
          footeremail && footeremail.trim() !== ""
            ? footeremail.replace(
                "Adjunto le remito la documentación indicada.",
                `Adjunto le remito la documentación indicada:\n${albList}`
              )
            : `Adjunto le remito la documentación indicada:\n${albList}`;
        setPurchases(normalized);

        setFormEMAIL((prev) => ({
          ...prev,
          defaultStore_id: defaultStore.id,
          useraccount_id,
          store_EmailTo: emails || "",
          subjectEmail: "ENVÍO DE ALBARANES UNIFICADOS",
          contentEmail: footeremailUpdated || "",
        }));
      } catch {
        if (!cancelled && mountedRef.current) {
          setPurchases([]);
          setPdfBlob(null);
          dispatch(warningErrorAction(t("Text_IncorrectData")));
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          dispatch(setLoading(false));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    defaultStore.id,
    dispatch,
    footeremail,
    getDataToPDF,
    isEndedList,
    isModalOpen,
    t,
    token,
    useraccount_id,
  ]);

  useEffect(() => {
    if (!isModalOpen) {
      setPdfBlob(null);
      return;
    }
    if (!hasPurchasesWithLines) {
      setPdfBlob(null);
      return;
    }
    try {
      const blob = generatePurchaseSendEmailPdf(purchases, headerPrinterPurchase || "");
      setPdfBlob(blob || null);
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      setPdfBlob(null);
    }
  }, [hasPurchasesWithLines, headerPrinterPurchase, isModalOpen, purchases]);

  const showDropdown = filteredEmail.length > 0;
  if (!isModalOpen) return null;

  return (
    <div className="container-fluid email-send-wrapper">
      <Box sx={{ bgcolor: "#ffffff", width: "1050px" }}>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="email-card-header">
            <div className="email-header-content">
              <div className="email-header-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <div className="email-header-text">
                <div className="email-header-title">{t("Text_List_EMAIL")}:</div>
              </div>
              <div className="email-card-actions">
                {!isLoadingData && (
                  <>
                    <button
                      type="button"
                      className="email-send-btn"
                      onClick={handleSubmitSendEmail}
                    >
                      {t("Button_SEND")}
                      <SendIcon sx={{ fontSize: 22, color: "white" }} />
                    </button>
                    <button
                      type="button"
                      className="email-cancel-btn"
                      onClick={resetStateAndClose}
                    >
                      {t("Button_Cancel")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {!isLoadingData ? (
            <>
              <div className="email-card-form">
                <div className="row g-3">
                  <div className="col-12 email-input" style={{ position: "relative" }}>
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_EmailsTo")} :
                    </label>
                    <TextField
                      id="store_EmailTo"
                      name="store_EmailTo"
                      value={formEMAIL.store_EmailTo}
                      onChange={onChangeEmailTo}
                      onKeyDown={handleKeyDownEmail}
                      onBlur={(event) => {
                        if (
                          !event.relatedTarget ||
                          !event.relatedTarget.closest(".email-suggest-dropdown")
                        ) {
                          handleBlurSuggestions(event);
                        }
                      }}
                      label={
                        errorsEMAIL.store_EmailTo
                          ? t(errorsEMAIL.store_EmailTo)
                          : t("PlaceHolder_SENDEMAIL_EmailsTo")
                      }
                      placeholder={t("PlaceHolder_SENDEMAIL_EmailsTo")}
                      variant="standard"
                      inputProps={{
                        style: { width: emailToWidth },
                        maxLength: 100,
                      }}
                      InputLabelProps={{
                        style: { fontSize: 18, background: "#FFFFFF" },
                      }}
                    />
                    {showDropdown && (
                      <div
                        className="email-suggest-dropdown"
                        onMouseDown={(event) => event.preventDefault()}
                      >
                        {filteredEmail.map((store, index) => {
                          const isActive = index === highlightedIndexEmail;
                          return (
                            <MenuItem
                              key={store.id || `${store.email}-${index}`}
                              onClick={() => handleSelectEmail(store)}
                              onMouseDown={(event) => event.preventDefault()}
                              style={{
                                cursor: "pointer",
                                padding: "10px 12px",
                                borderBottom: "1px solid #eee",
                                fontSize: 18,
                                backgroundColor: isActive ? "#4e73df" : "transparent",
                                color: isActive ? "white" : "black",
                              }}
                            >
                              {store.email}
                            </MenuItem>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="col-12 email-input">
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_Subject")}
                    </label>
                    <TextField
                      id="subjectEmail"
                      name="subjectEmail"
                      value={formEMAIL.subjectEmail ?? ""}
                      inputProps={{
                        style: { width: subjectWidth },
                        maxLength: 100,
                      }}
                      InputLabelProps={{
                        style: { fontSize: 18, background: "#FFFFFF" },
                      }}
                      label={
                        errorsEMAIL.subjectEmail
                          ? t(errorsEMAIL.subjectEmail)
                          : t("PlaceHolder_SENDEMAIL_Subject")
                      }
                      placeholder={t("PlaceHolder_SENDEMAIL_Subject")}
                      variant="standard"
                      onBlur={handleBlurDataEditEmail}
                      onChange={handleChangeDataEditEmail}
                    />
                  </div>
                  <div className="col-12">
                    <label className="text-primary" style={{ fontSize: 18 }}>
                      {t("Text_SENDEMAIL_Content")} :
                    </label>
                    <Textarea
                      id="contentEmail"
                      name="contentEmail"
                      value={formEMAIL.contentEmail}
                      className="email-textarea"
                      placeholder={t("PlaceHolder_SENDEMAIL_Content")}
                      sx={{ width: 1000, fontSize: 18 }}
                      variant="standard"
                      onBlur={handleBlurDataEditEmail}
                      onChange={handleChangeDataEditEmail}
                      maxRows={8}
                    />
                  </div>
                </div>
              </div>
              <div className="email-card-pdf">
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="PDF Preview"
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                ) : null}
              </div>
            </>
          ) : (
            <LoadingNews />
          )}
        </form>
      </Box>
      <br />
    </div>
  );
};

PurchaseDeliverySendEmailList.propTypes = {
  getDataToPDF: PropTypes.any,
  showModalPurchaseSendEmailList: PropTypes.bool,
  typeOfList: PropTypes.string 
};

PurchaseDeliverySendEmailList.defaultProps = {
  getDataToPDF: null,
  showModalPurchaseSendEmailList: false,
  typeOfList: "" 
};

export default PurchaseDeliverySendEmailList;